import { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { selectIsCallCenter } from '@store/selectors/is-call-center.selector';
import { ResetIsCallCenter } from '@store/actions/is-call-center.actions';
import { TenantService } from '@lib/services/tenant.service';
import { Store } from '@ngrx/store';
import { clearTenantKey } from '@store/actions/tenant.actions';
import { AuthService } from '@lib/services/auth/auth.service';

export const authInterceptor: HttpInterceptorFn = (request, next) => {
    const router = inject(Router);
    const activatedRoute = inject(ActivatedRoute);
    const dialog = inject(MatDialog);
    const snackBar = inject(MatSnackBar);
    const tenantService = inject(TenantService);
    const store = inject(Store);
    const authService = inject(AuthService);
    const tenantKey = tenantService.getTenantKey() || '';
    const accessToken = localStorage.getItem('accessToken') || '';
    const xConsumerUsername = activatedRoute.snapshot.firstChild?.paramMap.get('tenant') || '';
    return store.select(selectIsCallCenter).pipe(
        take(1),
        switchMap((callCenter) => {
            if (!accessToken && !callCenter) {
                request = request.clone({ headers: request.headers.set('X-Consumer-Username', xConsumerUsername) });
            }
            if (accessToken && !request.headers.get('x-no-authorization')) {
                request = request.clone({ headers: request.headers.set('Authorization', `Bearer ${accessToken}`) });
            }

            if (request.headers.has('x-no-authorization')) {
                request = request.clone({ headers: request.headers.delete('x-no-authorization') });
            }

            if (authService.isRefreshing && accessToken && !request.headers.get('x-no-authorization')) {
                return authService.refreshTokenSubject.pipe(
                    filter((token): token is string => token !== null),
                    take(1),
                    switchMap((newToken) =>
                        next(request.clone({ headers: request.headers.set('Authorization', `Bearer ${newToken}`) })),
                    ),
                );
            }

            return next(request).pipe(
                catchError((error) => {
                    const currentAccessToken = localStorage.getItem('accessToken');
                    if (error.status === 401 && currentAccessToken) {
                        return handle401Error(request, next, authService, router, store, dialog, snackBar, tenantKey);
                    }
                    if (error.status === 401 || (error.status === 403 && !currentAccessToken)) {
                        redirectToLogin(router, store, dialog, snackBar, tenantKey);
                    }
                    return throwError(() => error);
                }),
            );
        }),
    );
};

function handle401Error(request: HttpRequest<unknown>, next: HttpHandlerFn, authService: AuthService, router: Router, store: Store, dialog: MatDialog, snackBar: MatSnackBar, tenantKey: string): Observable<HttpEvent<unknown>> {
    if (authService.isRefreshing) {
        return authService.refreshTokenSubject.pipe(
            filter((token): token is string => token !== null),
            take(1),
            switchMap((token) => next(request.clone({ headers: request.headers.set('Authorization', `Bearer ${token}`) }))),
        );
    }

    authService.isRefreshing = true;
    authService.refreshTokenSubject.next(null);

    return authService.refreshToken(tenantKey).pipe(
        switchMap((response) => {
            const newToken = response.access_tokens?.[0]?.access_token;
            if (!newToken) {
                return throwError(() => new Error('Refresh token response missing access token'));
            }
            authService.isRefreshing = false;
            localStorage.setItem('accessToken', newToken);
            authService.refreshTokenSubject.next(newToken);
            return next(request.clone({ headers: request.headers.set('Authorization', `Bearer ${newToken}`) }));
        }),
        catchError((err) => {
            authService.isRefreshing = false;
            authService.refreshTokenSubject.next(null);
            redirectToLogin(router, store, dialog, snackBar, tenantKey);
            return throwError(() => err);
        }),
    );
}

function redirectToLogin(router: Router, store: Store, dialog: MatDialog, snackBar: MatSnackBar, tenantKey: string): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('selectedUser');
    dialog.closeAll();
    store.dispatch(new ResetIsCallCenter());
    store.dispatch(clearTenantKey());
    snackBar.open('Your session has expired. Please log in again.', 'Close', { duration: 5000 });
    router.navigateByUrl(`auth/${tenantKey}/login`);
}
